import React, { ComponentType } from "react";

import { CheckCircle, Edit, Info, Send, WarningAmber } from "icons";
import { color } from "minimum-ui/tokens";
import styled, { css } from "styled-components";
import { H4R2 } from "typography/headers";

import { Spinner } from "minimum-ui/core/Spinner/Spinner";

export type Variant =
  | "error"
  | "warning"
  | "success"
  | "info"
  | "shiny"
  | "lightBlue"
  | "blueInfo"
  | "blue"
  | "yellow"
  | "gray"
  | "empty";

export interface AlertProps {
  /**
   * Defines colors, borders, and icons
   */
  variant?: Variant;
  /**
   * Show loading indicator on the right?
   */
  loading?: boolean;
  /**
   * Allow overloading of styles
   */
  className?: string;
  /**
   * If true any children passed will not be wrapped in a <h4> tag. leave false if you are just passing text
   */
  complexChildren?: boolean;
}

const Alert: React.FC<AlertProps> = ({
  loading = false,
  variant = "info",
  children,
  className,
  complexChildren,
}) => {
  const icon = VariantIcons[variant];
  return (
    <AlertWrapper className={className} $variant={variant}>
      <Icon as={icon} />
      {complexChildren ? <div>{children}</div> : <Copy>{children}</Copy>}
      {loading && <Spinner size={20} />}
    </AlertWrapper>
  );
};

export const VariantIcons: Record<Variant, ComponentType> = {
  error: WarningAmber,
  warning: WarningAmber,
  success: CheckCircle,
  info: Info,
  shiny: Info,
  empty: Info,
  lightBlue: Info,
  blueInfo: Info,
  blue: Send,
  yellow: Edit,
  gray: Info,
};

export const VariantStyles: Record<Variant, Record<string, string>> = {
  error: {
    "--background": color.red[100],
    "--icon": color.red[300],
  },
  warning: {
    "--background": color.yellow[100],
    "--icon": color.yellow[300],
  },
  success: {
    "--background": color.green[100],
    "--icon": color.green[300],
  },
  info: {
    "--icon": color.neutral[500],
    "--background": color.neutral[100],
    "--border": `1px solid ${color.neutral[300]}`,
  },
  shiny: {
    "--icon": color.neutral[400],
    "--background": color.blue[100],
    "--border": `1px solid ${color.blue[300]}`,
  },
  empty: {
    "--background": color.neutral[100],
  },
  lightBlue: {
    "--background": color.blue[100],
    "--icon": color.neutral[500],
    "--border": `none`,
    "border-radius": "0px",
  },
  blueInfo: {
    "--background": color.background.info,
    "--icon": color.foreground.info,
    "--border": `1px solid ${color.blue[200]}`,
  },
  blue: {
    "--background": color.background.info,
    "--icon": color.foreground.info,
    "--border": `1px solid ${color.blue[200]}`,
  },
  yellow: {
    "--background": color.background.warning,
    "--icon": color.foreground.warning,
    "--border": `1px solid ${color.yellow[200]}`,
  },
  gray: {
    "--background": color.background.default,
    "--icon": color.neutral[400],
    "--border": `1px solid ${color.border.default}`,
  },
};

export const AlertWrapper = styled.div<{ $variant: Variant }>`
  background: var(--background, transparent);
  border-radius: 8px;
  padding: 12px;
  border: var(--border, 1px solid transparent);
  ${(props) => VariantStyles[props.$variant]};
  display: grid;
  grid-template-columns: 20px 1fr 20px;
  gap: 6px;

  ${({ $variant }) =>
    ($variant === "blueInfo" ||
      $variant === "blue" ||
      $variant === "yellow" ||
      $variant === "gray") &&
    css`
      > svg {
        position: relative;
        top: 3px;
        width: 16px;
        height: 16px;
      }
    `};
`;

export const Icon = styled.svg.attrs({ role: "img" })`
  width: 20px;
  height: 20px;
  flex: 0;
  color: var(--icon);
`;

export const Copy = styled(H4R2)`
  color: ${color.neutral[500]};
`;

export default Alert;
